import * as React from 'react';
import { Field } from 'redux-form';

import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import DateInput from 'af-fields/DateInput';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import DropdownEmployeeItem from 'af-components/DropdownEmployeeItem';

import WorkRequestUpsertFM from '../../formModel';
import { formSectionClass, formSectionClassColumn2, formSectionClassColumn4, formSectionClassRow } from '../../helpers';
import styles from '../../styles.module.scss';

type Props = {
	findAllSuperintendentsAndProjectManagers: () => Promise<EmployeeOptionVM[]>;
};

const filterOption = (option: EmployeeOptionVM, rawInput: string) => {
	const { firstName, lastName, uniqueId } = option;
	return `${firstName} ${lastName} ${uniqueId}`.toLowerCase().includes(rawInput.toLowerCase());
};

const SurveyDataSection: React.FC<Props> = ({ findAllSuperintendentsAndProjectManagers }) => {
	const [superintendentsAndProjectManagers, setSuperintendentsAndProjectManagers] = React.useState<EmployeeOptionVM[]>([]);
	const lazyLoadSuperintendentsAndProjectManagers = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedSuperintendentsAndProjectManagers = await findAllSuperintendentsAndProjectManagers();
			setSuperintendentsAndProjectManagers(fetchedSuperintendentsAndProjectManagers ?? []);
		}
	}, [findAllSuperintendentsAndProjectManagers]);

	const renderSelectedSIorPM = React.useCallback((option: EmployeeOptionVM) => <DropdownEmployeeItem option={option} showUserRole={false} />, []);

	const renderSIAndPMMenuItem = React.useCallback((option: EmployeeOptionVM) => <DropdownEmployeeItem option={option} showUserRole={true} />, []);

	const checkboxStyle = React.useMemo(() => [formSectionClassColumn2, styles['work-request-form__tab-content__form-section__row__centered-column']].join(' '), []);

	return (
		<div className={formSectionClass}>
			<SegmentLabel
				label="Survey Data"
				tooltip="Field survey details and site conditions required for job execution, including infrastructure specifications, water availability, and subcontractor requirements."
			/>
			<div className={formSectionClassRow}>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="surveyNeeded"
						label="Survey Needed"
						name={WorkRequestUpsertFM.getAttributeName('surveyNeeded')}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						filterable={true}
						filterBy={filterOption}
						id="surveyedById"
						label="Surveyed By"
						name={WorkRequestUpsertFM.getAttributeName('surveyedById')}
						onLazyLoad={lazyLoadSuperintendentsAndProjectManagers}
						options={superintendentsAndProjectManagers}
						placeholder="Select"
						propName="surveyedBy"
						renderMenuItem={renderSIAndPMMenuItem}
						renderSelected={renderSelectedSIorPM}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={DateInput}
						id="surveyDate"
						label="Survey Date"
						name={WorkRequestUpsertFM.getAttributeName('surveyDate')}
						originalDateFormat={TimeFormat.DATE_ONLY}
						placeholderText={TimeFormat.DATE_ONLY}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="waterAvailable"
						label="Water Available"
						name={WorkRequestUpsertFM.getAttributeName('waterAvailable')}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="waterSupplyInformation"
						label="Water Supply Information"
						name={WorkRequestUpsertFM.getAttributeName('waterSupplyInformation')}
						placeholder="Enter Water Supply Information"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="subcontractorNeeded"
						label="Subcontractor Needed"
						name={WorkRequestUpsertFM.getAttributeName('subcontractorNeeded')}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="subcontractorInformation"
						label="Subcontractor Information"
						name={WorkRequestUpsertFM.getAttributeName('subcontractorInformation')}
						placeholder="Enter Subcontractor Information"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="pipeSizes"
						label="Pipe Sizes"
						name={WorkRequestUpsertFM.getAttributeName('pipeSizes')}
						placeholder="Enter Text"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="pipeTypes"
						label="Pipe Types"
						name={WorkRequestUpsertFM.getAttributeName('pipeTypes')}
						placeholder="Enter Text"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="manholeDepths"
						label="Manhole Depths"
						name={WorkRequestUpsertFM.getAttributeName('manholeDepths')}
						placeholder="Enter Text"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="manholeFrameDiameters"
						label="Manhole Frame Diameters"
						name={WorkRequestUpsertFM.getAttributeName('manholeFrameDiameters')}
						placeholder="Enter Text"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn4}>
					<Field
						component={Input}
						id="informationsProvidedByCustomersBy"
						label="How Will Information be Provided by Customer?"
						name={WorkRequestUpsertFM.getAttributeName('informationsProvidedByCustomersBy')}
						placeholder="Enter Text"
						type="text"
					/>
				</div>
			</div>
		</div>
	);
};

export default React.memo(SurveyDataSection);
